import React from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { downloadReportFile } from "./reportDownload";
import { C } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// Masks the shared icon assets (public/icons) to a solid color, same
// technique as the sidebar nav icons — works regardless of the source
// SVG's own stroke/fill color since CSS mask keys off the alpha channel.
function MaskIcon({ src, color, size = 15 }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: size,
        height: size,
        display: "inline-block",
        flexShrink: 0,
        backgroundColor: color,
        mask: `url(${src}) center / contain no-repeat`,
        WebkitMask: `url(${src}) center / contain no-repeat`,
      }}
    />
  );
}

// Shared "View Report" / "Download" pill actions — used on the order
// timeline cards (OrderTestActionChips) and the flat Reports tables alike,
// so the report action UI reads as one system everywhere it appears.
// `getLink(id)` resolves a fresh presigned URL on each click — never cached,
// it's a 5-minute S3 URL.
function ViewDownloadButtons({ id, filename, getLink }) {
  const { t } = useTranslation();
  const [loadingAction, setLoadingAction] = React.useState(null); // "view" | "download" | null
  const [errorMsg, setErrorMsg] = React.useState("");

  const handle = async (action) => {
    setErrorMsg("");
    setLoadingAction(action);
    try {
      const res = await getLink(id);
      const viewUrl = res?.data?.view_url;
      if (!viewUrl) {
        const noLinkErr = new Error("No report link returned");
        noLinkErr.noLink = true;
        throw noLinkErr;
      }
      if (action === "view") {
        window.open(viewUrl, "_blank", "noopener");
      } else {
        await downloadReportFile(viewUrl, filename);
      }
    } catch (err) {
      setErrorMsg(
        err.status === 404
          ? t("portal.viewDownloadButtons.reportNotFound")
          : err.noLink
          ? t("portal.viewDownloadButtons.fetchLinkError")
          : err.message || t("portal.viewDownloadButtons.fetchLinkError")
      );
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      {errorMsg && <Typography sx={{ fontSize: "0.75rem", color: "#DC2626" }}>{errorMsg}</Typography>}
      <Chip
        clickable
        size="small"
        onClick={() => handle("view")}
        icon={
          loadingAction === "view" ? (
            <CircularProgress size={13} sx={{ color: C.primary, ml: 0.5 }} />
          ) : (
            <MaskIcon src="/icons/ic_view.svg" color={C.primary} />
          )
        }
        label={t("portal.viewDownloadButtons.viewReport")}
        sx={{
          bgcolor: "#FFFFFF",
          color: C.primary,
          fontWeight: 600,
          borderRadius: "8px",
          border: `1px solid ${C.primary}40`,
          pl: 1,
          pr: 1.25,
          "& .MuiChip-icon": { ml: 0.25 },
          "&:hover": { bgcolor: C.tintBg },
        }}
      />
      <Chip
        clickable
        size="small"
        onClick={() => handle("download")}
        icon={
          loadingAction === "download" ? (
            <CircularProgress size={13} sx={{ color: "#fff", ml: 0.5 }} />
          ) : (
            <MaskIcon src="/icons/ic_download.svg" color="#fff" />
          )
        }
        label={t("portal.viewDownloadButtons.download")}
        sx={{
          bgcolor: C.primary,
          color: "#fff",
          fontWeight: 600,
          borderRadius: "8px",
          pl: 1,
          pr: 1.25,
          "& .MuiChip-icon": { ml: 0.25 },
          "&:hover": { bgcolor: "#0f3563" },
        }}
      />
    </Box>
  );
}

export default ViewDownloadButtons;
